/**
 * 司机端登录页面
 * 手机端优先设计
 * Frontend version: 2026.03.08.001
 * 
 * 使用集中式API配置和DriverAPI服务
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL, DriverAPI, TokenManager, isProductionEnvironment, isPreviewEnvironment } from './config';
import {fleetRequest, saveFleetSession} from '../fleetDriver/fleetDriverApi';
import DriverBrandHeader from './DriverBrandHeader';
import DriverLanguageDropdown from './DriverLanguageDropdown';
import chauffeurAvatar from '../../assets/driver-chauffeur-avatar.png';
import bigTaxiDriverLogo from '../../assets/big-taxi-driver-logo.png';
import {
  clearRememberedDriverCredentials,
  getInitialRememberedPhone,
  hasRememberedCredentialsHint,
  isDriverBiometricLoginEnabled,
  loadBiometricDriverCredentials,
  loadRememberedDriverCredentials,
  saveRememberedDriverCredentials,
} from '../../services/driverApp/credentialService';
import { getDriverDeviceInfo } from '../../services/driverApp/deviceService';
import { getLocalizedDriverApiError } from './driverFeatureI18n';
import { getDriverHtmlLanguage, getDriverLanguage, setDriverLanguagePreference } from './driverI18n';

const FRONTEND_VERSION = "2026.03.08.001";
const DRIVER_LANGUAGE_KEY = 'driver_language';
const DRIVER_INTRO_LANGUAGE_DELAY_MS = 1350;
const FLEET_TOKEN_KEY = 'bigtaxi-fleet-token';

const driverLanguageOptions = [
  {
    code: 'zh_cn',
    label: '简体中文',
    caption: '司机端使用简体中文显示',
    actionLabel: '进入',
  },
  {
    code: 'zh_tw',
    label: '繁體中文',
    caption: '司機端使用繁體中文顯示',
    actionLabel: '進入',
  },
  {
    code: 'ja',
    label: '日本語',
    caption: 'ドライバー画面を日本語で表示',
    actionLabel: '開始',
  },
  {
    code: 'en',
    label: 'English',
    caption: 'Use the driver app in English',
    actionLabel: 'Enter',
  },
];

const driverLoginText = {
  zh_cn: {
    portal: 'Driver Portal',
    introKicker: 'BIG TAXI DRIVER',
    languageTitle: '选择语言',
    languageSubtitle: '请选择司机端显示语言',
    enter: '进入',
    loginTitle: '司机登录',
    phoneLabel: '手机号',
    phonePlaceholder: '请输入手机号',
    passwordLabel: '密码',
    passwordPlaceholder: '请输入密码',
    rememberAccount: '记住账号和密码',
    loginWithFaceId: '通过 Face ID 登录',
    faceIdReason: '请使用 Face ID 登录 Big Taxi Driver',
    faceIdFailed: 'Face ID 登录失败，请使用密码登录',
    login: '登录',
    loggingIn: '登录中...',
    required: '请输入手机号和密码',
    failed: '登录失败，请检查手机号和密码',
    wrongCredentials: '手机号或密码错误',
    disabled: '账号已被禁用，请联系管理员',
    serverError: '系统错误，请稍后重试',
    networkError: '网络异常，请检查网络连接',
    footer: 'Big Taxi',
    avatarAlt: '司机头像',
  },
  zh_tw: {
    portal: 'Driver Portal',
    introKicker: 'BIG TAXI DRIVER',
    languageTitle: '選擇語言',
    languageSubtitle: '請選擇司機端顯示語言',
    enter: '進入',
    loginTitle: '司機登入',
    phoneLabel: '手機號',
    phonePlaceholder: '請輸入手機號',
    passwordLabel: '密碼',
    passwordPlaceholder: '請輸入密碼',
    rememberAccount: '記住帳號和密碼',
    loginWithFaceId: '透過 Face ID 登入',
    faceIdReason: '請使用 Face ID 登入 Big Taxi Driver',
    faceIdFailed: 'Face ID 登入失敗，請使用密碼登入',
    login: '登入',
    loggingIn: '登入中...',
    required: '請輸入手機號和密碼',
    failed: '登入失敗，請檢查手機號和密碼',
    wrongCredentials: '手機號或密碼錯誤',
    disabled: '帳號已被停用，請聯絡管理員',
    serverError: '系統錯誤，請稍後重試',
    networkError: '網路異常，請檢查網路連線',
    footer: 'Big Taxi',
    avatarAlt: '司機頭像',
  },
  ja: {
    portal: 'Driver Portal',
    introKicker: 'BIG TAXI DRIVER',
    languageTitle: '言語を選択',
    languageSubtitle: 'ドライバーアプリの表示言語を選択してください',
    enter: '開始',
    loginTitle: 'ドライバーログイン',
    phoneLabel: '携帯番号',
    phonePlaceholder: '携帯番号を入力',
    passwordLabel: 'パスワード',
    passwordPlaceholder: 'パスワードを入力',
    rememberAccount: 'アカウントとパスワードを保存',
    loginWithFaceId: 'Face IDでログイン',
    faceIdReason: 'Big Taxi DriverにログインするためFace IDで認証してください',
    faceIdFailed: 'Face IDログインに失敗しました。パスワードでログインしてください',
    login: 'ログイン',
    loggingIn: 'ログイン中...',
    required: '携帯番号とパスワードを入力してください',
    failed: 'ログインに失敗しました。携帯番号とパスワードを確認してください',
    wrongCredentials: '携帯番号またはパスワードが正しくありません',
    disabled: 'アカウントが無効です。管理者に連絡してください',
    serverError: 'システムエラーです。しばらくしてから再試行してください',
    networkError: 'ネットワークエラーです。接続を確認してください',
    footer: 'Big Taxi',
    avatarAlt: 'ドライバー画像',
  },
  en: {
    portal: 'Driver Portal',
    introKicker: 'BIG TAXI DRIVER',
    languageTitle: 'Choose Language',
    languageSubtitle: 'Select the display language for the driver app',
    enter: 'Enter',
    loginTitle: 'Driver Login',
    phoneLabel: 'Mobile Number',
    phonePlaceholder: 'Enter mobile number',
    passwordLabel: 'Password',
    passwordPlaceholder: 'Enter password',
    rememberAccount: 'Remember account and password',
    loginWithFaceId: 'Sign in with Face ID',
    faceIdReason: 'Authenticate with Face ID to sign in to Big Taxi Driver',
    faceIdFailed: 'Face ID sign-in failed. Please use your password',
    login: 'Login',
    loggingIn: 'Signing in...',
    required: 'Please enter mobile number and password',
    failed: 'Login failed. Please check your mobile number and password',
    wrongCredentials: 'Mobile number or password is incorrect',
    disabled: 'This account is disabled. Please contact dispatch',
    serverError: 'System error. Please try again later',
    networkError: 'Network error. Please check your connection',
    footer: 'Big Taxi',
    avatarAlt: 'Driver avatar',
  },
};

function readLocalStorage(key, fallback = '') {
  try {
    return localStorage.getItem(key) || fallback;
  } catch {
    return fallback;
  }
}

function writeLocalStorage(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Ignore storage failures in restricted WebView/private contexts.
  }
}

const entryCopy = {
  zh_cn: { title: '选择入口', driver: '司机登录', fleet: '车队登录', register: '注册司机或车队', hint: '车队主可管理名下司机并分配订单', back: '返回入口' },
  zh_tw: { title: '選擇入口', driver: '司機登入', fleet: '車隊登入', register: '註冊司機或車隊', hint: '車隊主管理旗下司機並分配訂單', back: '返回入口' },
  ja: { title: '入口を選択', driver: 'ドライバーログイン', fleet: 'フリートログイン', register: 'ドライバー・フリート登録', hint: 'フリート管理者は所属ドライバーと配車を管理できます', back: '入口に戻る' },
  en: { title: 'Choose access', driver: 'Driver sign in', fleet: 'Fleet sign in', register: 'Register driver or fleet', hint: 'Fleet managers can assign orders to their drivers', back: 'Back to access options' },
};

export default function DriverLogin({ onLogin, onRegister, initialLanguage }) {
  const [language, setLanguage] = useState(() => {
    const savedLanguage = initialLanguage || getDriverLanguage();
    return savedLanguage === 'zh' ? 'zh_cn' : savedLanguage;
  });
  const [introDismissed, setIntroDismissed] = useState(false);
  const [introPhase, setIntroPhase] = useState('intro');
  const [phone, setPhone] = useState(() => getInitialRememberedPhone());
  const [password, setPassword] = useState('');
  const [rememberCredentials, setRememberCredentials] = useState(() => {
    return hasRememberedCredentialsHint() || true;
  });
  const [biometricLoginEnabled, setBiometricLoginEnabled] = useState(() => isDriverBiometricLoginEnabled());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [entryMode, setEntryMode] = useState('chooser');
  const text = driverLoginText[language] || driverLoginText.zh_cn;
  const entry = entryCopy[language] || entryCopy.zh_cn;

  useEffect(() => {
    document.documentElement.lang = getDriverHtmlLanguage(language);
    setDriverLanguagePreference(language);
    document.title = 'Big Taxi Driver';
  }, [language]);

  // 页面加载时显示环境信息
  useEffect(() => {
    console.log(`[DriverLogin] ============================================`);
    console.log(`[DriverLogin] Frontend version: ${FRONTEND_VERSION}`);
    console.log(`[DriverLogin] API URL: ${API_BASE_URL}`);
    console.log(`[DriverLogin] Environment: ${isProductionEnvironment() ? 'PRODUCTION' : isPreviewEnvironment() ? 'PREVIEW' : 'LOCAL'}`);
    console.log(`[DriverLogin] ============================================`);
  }, []);

  useEffect(() => {
    const isCapacitorLogin = document.documentElement.classList.contains('capacitor-ios-app');

    document.body.classList.add('driver-login-active');
    if (!isCapacitorLogin) {
      document.body.classList.add('driver-web-login-active');
    }

    if (!isCapacitorLogin) {
      return () => {
        document.body.classList.remove('driver-login-active');
        document.body.classList.remove('driver-web-login-active');
      };
    }

    const preventLoginOverscroll = (event) => {
      const target = event.target;
      if (target?.closest?.('input, textarea, select, .driver-language-dropdown-menu, .driver-opening-language-panel')) {
        return;
      }
      event.preventDefault();
    };

    document.addEventListener('touchmove', preventLoginOverscroll, { passive: false });

    return () => {
      document.body.classList.remove('driver-login-active');
      document.body.classList.remove('driver-web-login-active');
      document.removeEventListener('touchmove', preventLoginOverscroll);
    };
  }, []);

  useEffect(() => {
    if (introDismissed || introPhase !== 'intro') return undefined;
    const timer = window.setTimeout(() => {
      setIntroPhase('language');
    }, DRIVER_INTRO_LANGUAGE_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [introDismissed, introPhase]);

  useEffect(() => {
    const resetOpeningGateOnResume = () => {
      if (document.visibilityState !== 'visible') return;
      setIntroDismissed(false);
      setIntroPhase('intro');
    };

    document.addEventListener('visibilitychange', resetOpeningGateOnResume);
    return () => {
      document.removeEventListener('visibilitychange', resetOpeningGateOnResume);
    };
  }, []);

  useEffect(() => {
    let active = true;
    loadRememberedDriverCredentials().then((credentials) => {
      if (!active) return;
      if (credentials.phone) {
        setPhone(credentials.phone);
      }
      if (credentials.password && !isDriverBiometricLoginEnabled()) {
        setPassword(credentials.password);
        setRememberCredentials(true);
      } else if (credentials.password) {
        setRememberCredentials(true);
      }
      setBiometricLoginEnabled(isDriverBiometricLoginEnabled());
    });
    return () => {
      active = false;
    };
  }, []);

  const chooseLanguage = (option) => {
    setLanguage(option.code);
    setDriverLanguagePreference(option.code);
    writeLocalStorage(DRIVER_LANGUAGE_KEY, option.code);
    setIntroPhase('leaving');
    window.setTimeout(() => {
      setIntroDismissed(true);
    }, 620);
  };

  const switchLanguage = (code) => {
    setLanguage(code);
    setDriverLanguagePreference(code);
    writeLocalStorage(DRIVER_LANGUAGE_KEY, code);
  };

  const submitLogin = async ({ nextPhone = phone, nextPassword = password, rememberNextCredentials = rememberCredentials } = {}) => {
    const normalizedPhone = (nextPhone || '').trim();
    const normalizedPassword = nextPassword || '';

    if (!normalizedPhone || !normalizedPassword) {
      setError(text.required);
      return;
    }

    setError('');
    setLoading(true);

    try {
      console.log('[DriverLogin] Attempting login via DriverAPI...');

      const deviceInfo = await getDriverDeviceInfo().catch((deviceError) => {
        console.warn('[DriverLogin] Device info unavailable:', deviceError);
        return {};
      });
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 10000);
      let realm;
      try {
        const response = await fetch(`${API_BASE_URL}/driver/account-realm`, {
          method: 'POST', credentials: 'omit', signal: controller.signal,
          headers: {'Content-Type':'application/json'}, body: JSON.stringify({phone:normalizedPhone}),
        });
        const routing = await response.json();
        if (!response.ok || !['shonan','fleet'].includes(routing.realm)) {
          const error = new Error('Account routing unavailable');
          error.response = {status:response.status,data:routing}; throw error;
        }
        realm = routing.realm;
      } finally { clearTimeout(timer); }
      // Resolve ownership before sending the password. Never retry credentials in another realm.
      const result = realm === 'fleet'
        ? await fleetRequest('POST','/driver/login',{phone:normalizedPhone,password:normalizedPassword,device_info:deviceInfo})
        : await DriverAPI.login(normalizedPhone, normalizedPassword, deviceInfo);

      console.log('[DriverLogin] Login successful');
      console.log('[DriverLogin] Calling onLogin callback...');

      try {
        if (rememberNextCredentials) {
          await saveRememberedDriverCredentials({
            phone: normalizedPhone,
            password: normalizedPassword,
          });
        } else {
          await clearRememberedDriverCredentials();
          setBiometricLoginEnabled(false);
        }
      } catch (credentialError) {
        console.warn('[DriverLogin] Credential storage failed after successful login:', credentialError);
      }

      // Do not let a regular driver inherit a previous fleet-owner session.
      localStorage.removeItem(FLEET_TOKEN_KEY);
      if (realm === 'fleet') {
        TokenManager.clearAll();
        saveFleetSession(result);
      } else {
        onLogin(result.driver, result.access_token);
      }

    } catch (err) {
      console.error('[DriverLogin] Login error:', err.response?.status, err.message);

      let message = text.failed;
      if (err.response?.status === 401) {
        message = text.wrongCredentials;
      } else if (err.response?.status === 403) {
        message = text.disabled;
      } else if (err.response?.status === 500) {
        message = text.serverError;
      } else if (!err.response) {
        message = text.networkError;
      } else if (err.response?.data?.detail) {
        message = getLocalizedDriverApiError(err, text.failed, language);
      }

      setError(message);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    await submitLogin();
  };

  const submitFleetLogin = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const normalizedPhone = (phone || '').trim();
    if (!normalizedPhone || !password) {
      setError(text.required);
      return;
    }

    setError('');
    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/fleet/login`, {
        phone: normalizedPhone,
        password,
      });
      if (rememberCredentials) {
        await saveRememberedDriverCredentials({ phone: normalizedPhone, password });
      }
      // Preserve the fleet session so its management page remains available
      // after switching screens or restarting the installed app.
      localStorage.setItem(FLEET_TOKEN_KEY, response.data.access_token);
      onLogin(response.data.driver, response.data.access_token);
    } catch (err) {
      setError(getLocalizedDriverApiError(err, text.failed, language));
      setLoading(false);
    }
  };

  const handleBiometricLogin = async () => {
    setError('');
    setLoading(true);
    try {
      const credentials = await loadBiometricDriverCredentials(text.faceIdReason);
      setPhone(credentials.phone);
      setPassword(credentials.password);
      setRememberCredentials(true);
      await submitLogin({
        nextPhone: credentials.phone,
        nextPassword: credentials.password,
        rememberNextCredentials: true,
      });
    } catch (err) {
      console.warn('[DriverLogin] Face ID login failed:', err);
      setError(text.faceIdFailed);
      setLoading(false);
    }
  };

  return (
    <div className="driver-luxury-portal driver-login-screen premium-shell flex flex-col">
      {!introDismissed && (
        <DriverOpeningLanguageGate
          phase={introPhase}
          text={text}
          value={language}
          onChooseLanguage={chooseLanguage}
        />
      )}

      <DriverBrandHeader
        pageTitle={text.portal}
        actions={
          <DriverLanguageDropdown
            options={driverLanguageOptions}
            value={language}
            onChange={switchLanguage}
            ariaLabel={text.languageSettings}
            testId="driver-language-dropdown"
            className="driver-login-language-dropdown"
          />
        }
      />

      {/* Login Form */}
      <div className="driver-login-body flex-1 flex items-center justify-center p-4">
        <div className="driver-login-card-wrap w-full max-w-sm">
          <div className="premium-card p-6">
            <div className="text-center mb-6">
              <img
                src={chauffeurAvatar}
                alt={text.avatarAlt}
                className="mx-auto mb-3 h-24 w-24 rounded-full object-cover shadow-[0_0_34px_rgba(246,217,130,0.18)]"
              />
              <h2 className="text-xl font-bold text-foreground">{entryMode === 'chooser' ? entry.title : entryMode === 'fleet' ? entry.fleet : text.loginTitle}</h2>
            </div>

            {error && (
              <div className="mb-4 premium-chip-destructive px-3 py-2 rounded-lg text-sm">
                {error}
              </div>
            )}

            {entryMode === 'chooser' ? (
              <div className="space-y-3">
                <button type="button" className="premium-button w-full bg-primary py-3 text-primary-foreground" onClick={() => setEntryMode('driver')} data-testid="driver-entry-login-button">{entry.driver}</button>
                <button type="button" className="premium-button w-full border border-primary/40 py-3 text-foreground" onClick={() => { setEntryMode('fleet'); setError(''); }} data-testid="fleet-entry-login-button">{entry.fleet}</button>
                <button type="button" className="w-full py-2 text-sm font-semibold text-primary" onClick={onRegister} data-testid="driver-entry-register-button">{entry.register}</button>
                <p className="pt-2 text-center text-xs text-muted-foreground">{entry.hint}</p>
              </div>
            ) : <form onSubmit={entryMode === 'fleet' ? submitFleetLogin : handleSubmit} className="space-y-4">
              <button type="button" className="text-sm text-muted-foreground" onClick={() => { setEntryMode('chooser'); setError(''); }}>{entry.back}</button>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {text.phoneLabel}
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="premium-input text-base"
                  placeholder={text.phonePlaceholder}
                  data-testid="driver-phone-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {text.passwordLabel}
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="premium-input text-base"
                  placeholder={text.passwordPlaceholder}
                  data-testid="driver-password-input"
                />
              </div>

              <label className="driver-remember-account-row">
                <input
                  type="checkbox"
                  checked={rememberCredentials}
                  onChange={(e) => setRememberCredentials(e.target.checked)}
                  className="driver-remember-account-checkbox"
                  data-testid="driver-remember-credentials-checkbox"
                />
                <span>{text.rememberAccount}</span>
              </label>

              {biometricLoginEnabled && (
                <button
                  type="button"
                  onClick={handleBiometricLogin}
                  disabled={loading}
                  className="driver-face-id-login-button"
                  data-testid="driver-face-id-login-button"
                >
                  <span aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none">
                      <path d="M8 3H6.5A2.5 2.5 0 0 0 4 5.5V7M16 3h1.5A2.5 2.5 0 0 1 20 5.5V7M8 21H6.5A2.5 2.5 0 0 1 4 18.5V17M16 21h1.5a2.5 2.5 0 0 0 2.5-2.5V17" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                      <path d="M9 9.2v1.1M15 9.2v1.1M8.7 15.1c1.8 1.7 4.8 1.7 6.6 0" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                    </svg>
                  </span>
                  {text.loginWithFaceId}
                </button>
              )}

              <button
                type="button"
                onClick={entryMode === 'fleet' ? submitFleetLogin : handleSubmit}
                disabled={loading}
                className="w-full premium-button bg-primary text-primary-foreground py-3 px-4 hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                data-testid="driver-login-button"
              >
                {loading ? text.loggingIn : entryMode === 'fleet' ? entry.fleet : text.login}
              </button>
            </form>}
          </div>

        </div>
      </div>
    </div>
  );
}

function DriverOpeningLanguageGate({ phase, text, value, onChooseLanguage }) {
  const initialLanguage = driverLanguageOptions.some((option) => option.code === value)
    ? value
    : driverLanguageOptions[0].code;
  const [selectedLanguage, setSelectedLanguage] = useState(initialLanguage);
  const selectedLanguageOption = driverLanguageOptions.find((option) => option.code === selectedLanguage) || driverLanguageOptions[0];
  const selectedLanguageText = driverLoginText[selectedLanguage] || text;

  useEffect(() => {
    if (driverLanguageOptions.some((option) => option.code === value)) {
      setSelectedLanguage(value);
    }
  }, [value]);

  return (
    <div
      className={`driver-opening-gate driver-opening-gate-${phase}`}
      data-testid="driver-opening-language-gate"
    >
      <div className="driver-opening-stage">
        <div className="driver-opening-radial" />
        <div className="driver-opening-cinematic-glow" aria-hidden="true" />
        <div className="driver-opening-gold-trace" aria-hidden="true" />
        {phase === 'leaving' && <div className="driver-opening-exit-glow" />}

        <div className="driver-opening-center" aria-hidden="true">
          <div className="driver-opening-brand">
            <span className="driver-opening-logo-wrap">
              <img src={bigTaxiDriverLogo} alt="" />
            </span>
            <h2>DRIVER PORTAL</h2>
            <p>Private Transfer & Charter</p>
          </div>
        </div>

        {phase === 'language' && (
          <div className="driver-opening-language-panel">
            <div className="driver-opening-language-dropdown-card">
              <span className="driver-opening-language-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="8" />
                  <path d="M4 12h16M12 4c2.2 2.2 3.2 4.8 3.2 8s-1 5.8-3.2 8M12 4c-2.2 2.2-3.2 4.8-3.2 8s1 5.8 3.2 8" />
                </svg>
              </span>
              <div className="driver-opening-language-copy">
                <strong>{selectedLanguageText.languageTitle}</strong>
                <small>{selectedLanguageOption.caption}</small>
              </div>
              <div
                className="driver-opening-language-options"
                role="radiogroup"
                aria-label={selectedLanguageText.languageTitle}
                data-testid="driver-intro-language-options"
              >
                {driverLanguageOptions.map((option) => {
                  const selected = option.code === selectedLanguage;
                  return (
                    <button
                      key={option.code}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      className={`driver-opening-language-option ${selected ? 'is-selected' : ''}`}
                      onClick={() => setSelectedLanguage(option.code)}
                      data-testid={`driver-intro-language-choice-${option.code}`}
                    >
                      <span className="driver-opening-language-option-dot" aria-hidden="true" />
                      <span>{option.label}</span>
                      {selected && <em aria-hidden="true">✓</em>}
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                onClick={() => onChooseLanguage(selectedLanguageOption)}
                className="driver-opening-language-submit"
                data-testid="driver-intro-language-submit"
              >
                <span>{selectedLanguageOption.actionLabel}</span>
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
